import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import Footer from "./Footer";
import {
  LayoutDashboard, Users, Building2, Briefcase, DollarSign, Gift, Award,
  FileText, ClipboardCheck, Clock, CalendarClock, FileBarChart, UserCog,
  LogOut, Menu, X, User, CalendarDays, Receipt, ChevronRight, ChevronDown, ChevronLeft,
  FolderOpen, Folder, Wallet, HandCoins, FileSignature, Globe, Code2, BookOpen, Palette, ListChecks, Bell, PiggyBank
} from "lucide-react";

// Tree groups for Admin - SAPPS style
const adminTree = [
  {
    label: "Dashboard",
    icon: LayoutDashboard,
    to: "/admin",
    type: "leaf",
  },
  {
    label: "Master Data",
    icon: FolderOpen,
    type: "group",
    children: [
      { to: "/admin/karyawan", label: "Karyawan", icon: Users },
      { to: "/admin/divisi", label: "Divisi", icon: Building2 },
      { to: "/admin/jabatan", label: "Jabatan", icon: Briefcase },
      { to: "/admin/kontrak", label: "Kontrak PKWT/PKWTT", icon: FileSignature },
      { to: "/admin/users", label: "User Management", icon: UserCog },
    ],
  },
  {
    label: "Kehadiran",
    icon: FolderOpen,
    type: "group",
    children: [
      { to: "/admin/absensi", label: "Absensi", icon: ClipboardCheck },
      { to: "/admin/izin", label: "Izin", icon: FileText },
      { to: "/admin/shift", label: "Shift / Jam Kerja", icon: Clock },
      { to: "/admin/atur-shift", label: "Atur Shift Karyawan", icon: CalendarClock },
    ],
  },
  {
    label: "Payroll",
    icon: FolderOpen,
    type: "group",
    children: [
      { to: "/admin/gaji", label: "Gaji", icon: DollarSign },
      { to: "/admin/tunjangan", label: "Tunjangan", icon: Gift },
      { to: "/admin/payroll", label: "Payroll & Slip Gaji", icon: Receipt },
    ],
  },
  {
    label: "Keuangan",
    icon: FolderOpen,
    type: "group",
    children: [
      { to: "/admin/keuangan", label: "Arus Kas", icon: Wallet },
      { to: "/admin/hutang", label: "Hutang Vendor", icon: HandCoins },
      { to: "/admin/kasbon", label: "Kasbon Karyawan", icon: HandCoins },
    ],
  },
  {
    label: "Kinerja",
    icon: FolderOpen,
    type: "group",
    children: [
      { to: "/admin/poin", label: "Poin Kinerja", icon: Award },
    ],
  },
  {
    label: "Project",
    icon: FolderOpen,
    type: "group",
    children: [
      { to: "/admin/project-payment", label: "Payment Website", icon: Globe },
      { to: "/admin/payment-developer", label: "Payment Developer", icon: Code2 },
      { to: "/admin/payment-designer", label: "Payment Designer", icon: Palette },
      { to: "/admin/payment-listing", label: "Listing", icon: ListChecks },
    ],
  },
  {
    label: "Koperasi",
    icon: FolderOpen,
    type: "group",
    children: [
      { to: "/admin/koperasi", label: "Koperasi Karyawan", icon: Wallet },
      { to: "/admin/vendor-mobile-api-docs", label: "API Docs Mobile Vendor", icon: BookOpen },
    ],
  },
  {
    label: "Laporan",
    icon: FolderOpen,
    type: "group",
    children: [
      { to: "/admin/laporan", label: "Laporan Konsolidasi", icon: FileBarChart },
      { to: "/admin/laporan-pembuatan-web", label: "Laporan Pembuatan Web", icon: Globe },
    ],
  },
];

const karyawanTree = [
  {
    label: "Dashboard",
    icon: LayoutDashboard,
    to: "/karyawan",
    type: "leaf",
  },
  {
    label: "Kehadiran",
    icon: FolderOpen,
    type: "group",
    children: [
      { to: "/karyawan/absensi", label: "Absensi Saya", icon: ClipboardCheck },
      { to: "/karyawan/izin", label: "Izin Saya", icon: FileText },
      { to: "/karyawan/ajukan-izin", label: "Ajukan Izin", icon: CalendarDays },
      { to: "/karyawan/shift", label: "Jadwal Shift Saya", icon: Clock },
    ],
  },
  {
    label: "Payroll",
    icon: FolderOpen,
    type: "group",
    children: [
      { to: "/karyawan/slip-gaji", label: "Slip Gaji Saya", icon: Receipt },
      // Hanya berarti buat karyawan yang terdelegasi sebagai developer/designer (lihat
      // Developer.employee_id/Designer.employee_id di admin) — kalau tidak terhubung, halaman
      // ini sendiri yang menampilkan pesan "belum terhubung", menu tetap selalu tampil (sama
      // seperti "Koperasi Saya" di bawah yang juga selalu tampil terlepas ikut koperasi atau
      // tidak).
      { to: "/karyawan/pendapatan", label: "Pendapatan", icon: Code2 },
    ],
  },
  {
    label: "Koperasi",
    icon: FolderOpen,
    type: "group",
    children: [
      { to: "/karyawan/koperasi", label: "Koperasi Saya", icon: Wallet },
    ],
  },
  {
    label: "Kinerja",
    icon: FolderOpen,
    type: "group",
    children: [
      { to: "/karyawan/poin", label: "Poin Saya", icon: Award },
    ],
  },
  {
    label: "Akun",
    icon: FolderOpen,
    type: "group",
    children: [
      { to: "/karyawan/kontrak", label: "Kontrak Saya", icon: FileSignature },
      { to: "/karyawan/profil", label: "Profil Saya", icon: User },
    ],
  },
];

// Tree for client portal. Satu akun bisa tertaut ke Client individu (user.client_id) DAN
// Group Client (user.group_id) sekaligus (lihat _apply_portal_link di backend) — kalau
// keduanya terisi, tampilkan 2 menu project TERPISAH, dalam urutan ini: (1) "Project Group"
// (scope=group, ke /client/group), (2) "Project Anda" (scope=client, ke /client) —
// masing-masing memanggil ClientDashboard dengan scope eksplisit supaya tidak ambigu. Kalau
// cuma tertaut salah satu, tetap 1 menu "Dashboard & Project" seperti sebelumnya (perilaku
// lama, tidak berubah).
// "Notifikasi" (badge angka unread) HANYA muncul untuk akun yang tertaut Group Client, karena
// cuma Group Client yang bisa mengajukan revisi/menerima notifikasi terkait.
// Dibangun sebagai fungsi (bukan array statis) karena butuh user/unreadCount dari Layout.
function buildClientTree(user, unreadCount) {
  const hasClient = !!user?.client_id;
  const hasGroup = !!user?.group_id;
  const tree = [];
  // Menu Tabungan (khusus Kepala Group) selalu tepat di bawah menu project group-nya.
  const tabungan = { label: "Tabungan", icon: PiggyBank, to: "/client/tabungan", type: "leaf" };
  if (hasClient && hasGroup) {
    tree.push({ label: "Project Group", icon: Building2, to: "/client/group", type: "leaf" });
    tree.push(tabungan);
    tree.push({ label: "Project Anda", icon: LayoutDashboard, to: "/client", type: "leaf" });
  } else {
    tree.push({ label: "Dashboard & Project", icon: LayoutDashboard, to: "/client", type: "leaf" });
    if (hasGroup) tree.push(tabungan);
  }
  if (hasGroup) {
    tree.push({ label: "Notifikasi", icon: Bell, to: "/client/notifikasi", type: "leaf", badge: unreadCount });
  }
  tree.push({ label: "Profil & Password", icon: User, to: "/client/profile", type: "leaf" });
  return tree;
}

// Tree for vendor portal
const vendorTree = [
  {
    label: "POS & Produk",
    icon: LayoutDashboard,
    to: "/vendor",
    type: "leaf",
  },
  {
    label: "Profil & Password",
    icon: User,
    to: "/vendor/profile",
    type: "leaf",
  },
];

function TreeNode({ node, location, onNavigate }) {
  const [expanded, setExpanded] = useState(true);
  const Icon = node.icon;

  if (node.type === "leaf") {
    const active = location.pathname === node.to;
    return (
      <Link
        to={node.to}
        className={`vb-tree-leaf ${active ? "vb-tree-active" : ""}`}
        onClick={onNavigate}
        data-testid={`menu-${node.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
      >
        <span style={{ width: 12, display: "inline-block" }}></span>
        <Icon className="w-3.5 h-3.5" />
        <span style={{ flex: 1 }}>{node.label}</span>
        {!!node.badge && (
          <span
            style={{ background: "#d8000c", color: "white", fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 9, lineHeight: "13px" }}
            data-testid="sidebar-notif-badge"
          >
            {node.badge > 99 ? "99+" : node.badge}
          </span>
        )}
      </Link>
    );
  }

  return (
    <div>
      <div
        className="vb-tree-folder"
        onClick={() => setExpanded(!expanded)}
        data-testid={`folder-${node.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
      >
        {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        {expanded ? <FolderOpen className="w-3.5 h-3.5" style={{ color: "#FFC000" }} /> : <Folder className="w-3.5 h-3.5" style={{ color: "#FFC000" }} />}
        <span style={{ fontWeight: 600 }}>{node.label}</span>
      </div>
      {expanded && (
        <div className="vb-tree-children">
          {node.children.map((c) => {
            const ChildIcon = c.icon;
            const active = location.pathname === c.to;
            return (
              <Link
                key={c.to}
                to={c.to}
                className={`vb-tree-leaf ${active ? "vb-tree-active" : ""}`}
                onClick={onNavigate}
                data-testid={`menu-${c.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              >
                <span style={{ width: 18, display: "inline-block", borderLeft: "1px dashed #999", marginLeft: 5 }}></span>
                <ChildIcon className="w-3.5 h-3.5" />
                <span>{c.label}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Layout({ role }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  // Sidebar bisa disembunyikan & ditampilkan lagi (di desktop maupun mobile) supaya tidak
  // menutupi/mengambil ruang konten body — status disimpan di localStorage per browser.
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 768);
  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const [sidebarHidden, setSidebarHidden] = useState(() => {
    try { return localStorage.getItem("simak_sidebar_hidden") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("simak_sidebar_hidden", sidebarHidden ? "1" : "0"); } catch { /* ignore */ }
  }, [sidebarHidden]);

  // Badge unread notifikasi revisi — hanya relevan untuk akun Group Client (user.group_id).
  const isGroupClient = role === "client" && !!user?.group_id;
  const [unreadNotif, setUnreadNotif] = useState(0);
  useEffect(() => {
    if (!isGroupClient) return;
    let cancelled = false;
    api.get("/client/notifications/unread-count")
      .then(({ data }) => { if (!cancelled) setUnreadNotif(data?.count || 0); })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGroupClient, location.pathname]);

  const tree = role === "admin" ? adminTree : role === "client" ? buildClientTree(user, unreadNotif) : role === "vendor" ? vendorTree : karyawanTree;
  const title = role === "admin" ? "SIMAK - Admin" : role === "client" ? "SIMAK - Client Portal" : role === "vendor" ? "SIMAK - Vendor Cooperativa" : "SIMAK - Karyawan";

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const closeMobile = () => setOpen(false);
  const hideSidebar = () => { if (isDesktop) setSidebarHidden(true); else closeMobile(); };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", background: "#808080" }}>
      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
        {/* Mobile overlay */}
        {open && (
          <div
            className="vb-sidebar-overlay"
            onClick={closeMobile}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0, 0, 0, 0.5)",
              zIndex: 99,
            }}
          />
        )}

        {/* Sidebar — disembunyikan total (display:none, tidak makan ruang layout) di desktop
            kalau sidebarHidden true, supaya body content bisa melebar penuh. Di mobile perilaku
            slide-overlay yang sudah ada (open/closeMobile) tidak diubah sama sekali. */}
        <aside
          className={`vb-sidebar ${open ? "open" : ""}`}
          style={{
            position: isDesktop ? "relative" : "fixed",
            zIndex: 100,
            height: "100%",
            display: isDesktop && sidebarHidden ? "none" : undefined,
          }}
          data-testid="sidebar"
        >
          <div className="vb-sidebar-header" data-testid="sidebar-title" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
              <img src="/logo-fivehoster.png" alt="logo" style={{ width: 28, height: 28, borderRadius: 4, background: "white", padding: 1, flexShrink: 0 }} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700 }}>{title}</div>
                <div style={{ fontSize: 10, opacity: 0.85, fontWeight: 400 }}>PT. Fivehoster Karya Bangsa</div>
              </div>
            </div>
            <button
              className="vb-btn"
              onClick={hideSidebar}
              title="Sembunyikan sidebar"
              data-testid="sidebar-collapse-btn"
              style={{ padding: "2px 6px", background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.4)", color: "white", flexShrink: 0 }}
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
          </div>
          <div style={{ padding: 4, minHeight: "calc(100% - 53px)", display: "flex", flexDirection: "column" }}>
            <div style={{ fontSize: 11, color: "#666", padding: "6px 8px", borderBottom: "1px solid #ccc", marginBottom: 4, background: "#fafafa" }}>
              <div style={{ fontWeight: 600, color: "#003399" }}>{user?.name}</div>
              <div style={{ fontSize: 10, color: "#888" }}>{user?.email}</div>
            </div>
            <div style={{ padding: "0 2px" }}>
              {tree.map((node, i) => (
                <TreeNode key={i} node={node} location={location} onNavigate={closeMobile} />
              ))}
            </div>
            <div
              className="vb-tree-leaf"
              onClick={handleLogout}
              data-testid="menu-logout"
              style={{ marginTop: 8, borderTop: "1px solid #ccc", paddingTop: 8, color: "#d8000c", fontWeight: 600, cursor: "pointer" }}
            >
              <span style={{ width: 12, display: "inline-block" }}></span>
              <LogOut className="w-3.5 h-3.5" />
              <span>Logout</span>
            </div>
            {role === "vendor" && (
              <div className="vendor-sidebar-alert" data-testid="vendor-sidebar-license-alert">
                Software Simak ini adalah milik PT. FIVEHOSTER KARYA BANGSA tidak diperjual belikan dan tidak boleh dipindah tangankan ataupun digunakan tidak pada peruntukanya
              </div>
            )}
          </div>
        </aside>

        {/* Mobile menu button */}
        <div style={{
          position: "fixed",
          top: 10,
          left: 10,
          zIndex: 50,
        }}
          className="mobile-menu-btn"
        >
          <button
            className="vb-btn"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-btn"
          >
            {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>

        {/* Tombol tampilkan-lagi sidebar (desktop saja) — cuma dirender saat sidebar
            disembunyikan, jadi tidak pernah tumpang tindih dengan mobile-menu-btn di atas
            (mobile-menu-btn sendiri sudah otomatis display:none di layar desktop lewat CSS). */}
        {sidebarHidden && (
          <button
            className="vb-btn desktop-sidebar-reopen-btn"
            onClick={() => setSidebarHidden(false)}
            title="Tampilkan sidebar"
            data-testid="sidebar-reopen-btn"
            style={{ position: "fixed", top: 10, left: 10, zIndex: 50 }}
          >
            <Menu className="w-4 h-4" />
          </button>
        )}

        {/* Main content */}
        <main
          style={{
            flex: 1,
            overflow: "auto",
            padding: "12px 16px",
            background: "#a0a0a0",
            minWidth: 0,
          }}
          data-testid="main-content"
        >
          <Outlet />
        </main>
      </div>

      <Footer role={role} />
    </div>
  );
}
